import { useRef, useEffect, useCallback } from "react";
import {
	setBgmVolume,
	getBgmElement,
	getActiveBgmSystem,
	getBgmAudio,
} from "../utils/audioManager";

/**
 * useBgmDucking - Hook for managing background music ducking
 * Handles ducking and restoring BGM volume for recording scenarios
 */
export const useBgmDucking = () => {
	const previousBgmVolumeRef = useRef(null);

	// Duck BGM to a specific volume level
	const duckBgm = useCallback((targetVolume = 0.03) => {
		try {
			const activeSystem = getActiveBgmSystem();

			// Store current volume before ducking and duck
			if (activeSystem === "html5") {
				const htmlBgm = getBgmElement();
				if (htmlBgm && previousBgmVolumeRef.current === null) {
					previousBgmVolumeRef.current = htmlBgm.volume || 0.5;
					setBgmVolume(targetVolume);
				}
			} else if (activeSystem === "web") {
				const webBgm = getBgmAudio();
				if (webBgm && previousBgmVolumeRef.current === null) {
					previousBgmVolumeRef.current = webBgm.volume || 0.5;
					setBgmVolume(targetVolume);
				}
			}
		} catch (err) {
			console.warn("useBgmDucking: Failed to duck BGM:", err);
		}
	}, []);

	// Restore BGM to previous volume
	const restoreBgm = useCallback(() => {
		if (previousBgmVolumeRef.current !== null) {
			try {
				const previousVolume = previousBgmVolumeRef.current;
				setBgmVolume(previousVolume);
				previousBgmVolumeRef.current = null;
			} catch (err) {
				console.warn("useBgmDucking: Failed to restore BGM:", err);
			}
		}
	}, []);

	// Duck BGM when mode changes to audio
	const duckBgmForMode = useCallback(
		(mode) => {
			if (mode === "audio") {
				duckBgm(0.03); // Duck to 3% volume
			}
		},
		[duckBgm]
	);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			restoreBgm();
		};
	}, [restoreBgm]);

	return {
		duckBgm,
		restoreBgm,
		duckBgmForMode,
	};
};

export default useBgmDucking;
